import type { CSSProperties } from '@material-ui/styles'
import type { EditorProps } from './Editor'

/**
 * An editor is "inline" when it renders bare inside surrounding content with a bubble/floating
 * menu instead of the boxed toolbar look — currently only the Presentation v2 `HtmlEditor`.
 */
export const isInlineEditor = (props: Pick<EditorProps, 'bubbleMenuEnabled' | 'floatingMenuEnabled'>): boolean =>
  Boolean(props.bubbleMenuEnabled || props.floatingMenuEnabled)

/**
 * ProseMirror appends `<br class="ProseMirror-trailingBreak">` to text blocks the browser would
 * otherwise render with no height (empty paragraphs) or collapse (blocks ending in a hard break).
 * That `<br>` is a rendering hack, not content, so it must not add a visible line of its own —
 * with one exception, which is why this selector is narrowed rather than blanket:
 *
 * - On a paragraph that has *other* children (a block ending in a real hard break, which is what
 *   pasting from Word or Gmail produces) the break is pure decoration. Showing it renders a
 *   phantom trailing newline at the end of the content (#22034), so it stays hidden.
 * - On a genuinely *empty* paragraph (`:only-child`) it is the only inline content there is, and
 *   an element with no inline content generates no line box. No line box means no caret rect: the
 *   caret cannot be clicked into a blank line and native ArrowUp/ArrowDown walks straight past it
 *   (#22366) — the editor has no vertical-movement handler of its own to compensate. Inline
 *   pres-v2 editors need it visible for the same reason, one layer up: an unreachable blank line
 *   can never open the FloatingMenu (#21916).
 *
 * Height is *not* what this rule is for. `blankLineStyles` pins a blank line to exactly one line
 * via `min-height`, so showing this break adds no height on top (#22160).
 */
export const trailingBreakSelector = (): string => '& .ProseMirror-trailingBreak:not(:only-child)'

/** Nest under the selector that owns the ProseMirror markup. See {@link trailingBreakSelector}. */
export const trailingBreakStyles = (): Record<string, CSSProperties> => ({
  [trailingBreakSelector()]: {
    display: 'none'
  }
})
